import React from 'react';
import { Box, Button, Typography, LinearProgress, Alert, CardContent } from '@mui/material';
import { CloudUpload as CloudUploadIcon, Description as FileIcon } from '@mui/icons-material';
import { BRAND_COLORS, SEMANTIC_COLORS, alpha } from '../styles/theme';
import { formatFileSize } from '../utils/fileIcons';
import { useTranslation } from '../i18n';
import { DropzoneContainer, UploadButton, FilePreview } from './file-uploader/FileUploader.styled';
import { useFileUploader } from './file-uploader/useFileUploader';
import type { UploadOutcome } from './file-uploader/uploadResults';
import UploadMetadataDialog from './file-uploader/UploadMetadataDialog';
import AddOptionDialogs from './file-uploader/AddOptionDialogs';

interface FileUploaderProps {
  onUpload: (
    files: File[],
    metadata: {
      destinationPath: string;
      flightType: string;
      payloadId: number;
      pilotId: string;
      customerId: string;
      projectId: string;
      airspaceObserverId: string;
      payloadOperatorId: string;
      bypassValidation?: boolean;
    }
  ) => Promise<UploadOutcome | void>;
  onAnalyze: (file: File, flightType?: string) => Promise<void>;
  loading: boolean;
  allowedTypes?: string[];
  maxFiles?: number;
  currentPath?: string;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  onUpload,
  onAnalyze,
  loading,
  allowedTypes = ['.csv', '.txt', '.pdf', '.bin', '.log'],
  maxFiles = 10,
  currentPath = '',
}) => {
  const { t } = useTranslation();
  const uploader = useFileUploader({
    onUpload,
    onAnalyze,
    loading,
    allowedTypes,
    maxFiles,
    currentPath,
  });

  const {
    error,
    success,
    uploadProgress,
    selectedFiles,
    setSelectedFiles,
    getRootProps,
    getInputProps,
    isDragActive,
    removeFile,
    handleAnalyze,
    setShowMetadataDialog,
  } = uploader;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* File Dropzone */}
      <DropzoneContainer {...getRootProps()} isDragActive={isDragActive}>
        <input {...getInputProps()} />
        <CloudUploadIcon
          sx={{
            fontSize: 48,
            color: alpha(BRAND_COLORS.forestGreen.main, 0.6),
            mb: 2,
          }}
        />
        <Typography variant="h6" gutterBottom>
          {isDragActive ? t('fileManagement.dropFilesHere') : t('fileManagement.dragAndDropFiles')}
        </Typography>
        <Typography variant="body2" sx={{ color: SEMANTIC_COLORS.text.secondary, mb: 2 }}>
          {t('fileManagement.orClickToBrowse')}
        </Typography>
        <Typography variant="caption" sx={{ color: SEMANTIC_COLORS.text.secondary }}>
          {t('fileManagement.allowedFilesInfo', { types: allowedTypes.join(', '), maxFiles })}
        </Typography>
      </DropzoneContainer>

      {/* File Previews */}
      {selectedFiles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('fileManagement.selectedFiles', { count: selectedFiles.length })}
          </Typography>

          {selectedFiles.map((fileInfo, index) => (
            <FilePreview key={index}>
              <CardContent>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <FileIcon sx={{ mr: 2, color: BRAND_COLORS.forestGreen.main }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" noWrap>
                        {fileInfo.file.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: SEMANTIC_COLORS.text.secondary }}>
                        {formatFileSize(fileInfo.file.size)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                    {fileInfo.file.name.toLowerCase().endsWith('.csv') && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleAnalyze(fileInfo.file)}
                        disabled={loading}
                        sx={{
                          borderColor: BRAND_COLORS.yellow.main,
                          color: BRAND_COLORS.yellow.main,
                          '&:hover': {
                            borderColor: alpha(BRAND_COLORS.yellow.main, 0.8),
                            backgroundColor: alpha(BRAND_COLORS.yellow.main, 0.1),
                          },
                        }}
                      >
                        {t('fileManagement.analyze')}
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => removeFile(index)}
                      disabled={loading}
                    >
                      {t('fileManagement.remove')}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </FilePreview>
          ))}

          {/* Upload Progress */}
          {loading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: BRAND_COLORS.forestGreen.main,
                  },
                }}
              />
              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                {t('fileManagement.uploadingProgress', { progress: uploadProgress })}
              </Typography>
            </Box>
          )}

          {/* Upload Button */}
          {!loading && (
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <UploadButton
                variant="contained"
                onClick={() => setShowMetadataDialog(true)}
                disabled={selectedFiles.length === 0}
                startIcon={<CloudUploadIcon />}
              >
                {t('fileManagement.uploadFiles')}
              </UploadButton>

              <Button
                variant="outlined"
                onClick={() => setSelectedFiles([])}
                disabled={selectedFiles.length === 0}
              >
                {t('fileManagement.clearAll')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Metadata Dialog */}
      <UploadMetadataDialog
        open={uploader.showMetadataDialog}
        onClose={() => uploader.setShowMetadataDialog(false)}
        loading={loading}
        currentPath={currentPath}
        selectedFiles={selectedFiles}
        isXerAdmin={uploader.isXerAdmin}
        loadingOptions={uploader.loadingOptions}
        flightTypes={uploader.flightTypes}
        payloads={uploader.payloads}
        pilots={uploader.pilots}
        customers={uploader.customers}
        projects={uploader.projects}
        airspaceObservers={uploader.airspaceObservers}
        payloadOperators={uploader.payloadOperators}
        flightType={uploader.flightType}
        setFlightType={uploader.setFlightType}
        payload={uploader.payload}
        setPayload={uploader.setPayload}
        pilot={uploader.pilot}
        setPilot={uploader.setPilot}
        customer={uploader.customer}
        setCustomer={uploader.setCustomer}
        project={uploader.project}
        setProject={uploader.setProject}
        airspaceObserver={uploader.airspaceObserver}
        setAirspaceObserver={uploader.setAirspaceObserver}
        payloadOperator={uploader.payloadOperator}
        setPayloadOperator={uploader.setPayloadOperator}
        isTestMode={uploader.isTestMode}
        setIsTestMode={uploader.setIsTestMode}
        onAddPayload={() => uploader.setShowAddPayloadDialog(true)}
        onAddCustomer={() => uploader.setShowAddCustomerDialog(true)}
        onAddProject={() => uploader.setShowAddProjectDialog(true)}
        onUpload={uploader.handleUpload}
      />

      {/* Add Customer/Project/Payload Dialogs */}
      <AddOptionDialogs
        showAddCustomerDialog={uploader.showAddCustomerDialog}
        setShowAddCustomerDialog={uploader.setShowAddCustomerDialog}
        newCustomerName={uploader.newCustomerName}
        setNewCustomerName={uploader.setNewCustomerName}
        addingCustomer={uploader.addingCustomer}
        onAddCustomer={uploader.handleAddCustomer}
        showAddProjectDialog={uploader.showAddProjectDialog}
        setShowAddProjectDialog={uploader.setShowAddProjectDialog}
        newProjectName={uploader.newProjectName}
        setNewProjectName={uploader.setNewProjectName}
        addingProject={uploader.addingProject}
        onAddProject={uploader.handleAddProject}
        showAddPayloadDialog={uploader.showAddPayloadDialog}
        setShowAddPayloadDialog={uploader.setShowAddPayloadDialog}
        newPayloadName={uploader.newPayloadName}
        setNewPayloadName={uploader.setNewPayloadName}
        newPayloadWeight={uploader.newPayloadWeight}
        setNewPayloadWeight={uploader.setNewPayloadWeight}
        addingPayload={uploader.addingPayload}
        onAddPayload={uploader.handleAddPayload}
      />
    </Box>
  );
};

export default FileUploader;
